import {
    fetchAssignedOrders, fetchAvailableBoys, fetchAwaitingOrders, fetchCancelledOrders,
    fetchCompletedOrders, fetchDataFeed,
    fetchLateOrders,
    fetchNewOrders,
    fetchPickedUpOrders,
    fetchPreparingOrders,
} from "../service/api/orders";
import {
    arrayMove,
    rectSortingStrategy,
    SortableContext,
    useSortable,
} from "@dnd-kit/sortable";
import {closestCenter, DndContext} from "@dnd-kit/core";
import "../css/Dashboard.css";

import {
    CheckCircle2, XCircle, IndianRupee,
    Timer, Clock, AlertTriangle, Package,
    Store, UserCheck, BellRing, Truck, Inbox
} from 'lucide-react';
import TrackingMap from "../components/TrackingMap";
import NotificationToaster from "../components/NotificationToaster";

import React, {useCallback, useEffect, useState} from 'react';
import {useOrderAlerts} from "../hooks/useOrderAlerts";
import AvailablePartnersDiv from "../components/AvailablePartnersDiv";


const OrderSection = ({ updated, onDismiss, title, orders = [], loading }) => {
    const isEmpty = !loading && (!orders || orders.length === 0);
    const isFirstLoad = loading && orders === [];
    const skeletons = Array.from({ length: 2 });

    return (
        <section className="section-order">
            <div style={{ padding: '0px 20px' }}>
                <div className="section-header" style={{ justifyContent: "space-between" }}>
                    <p className="section-title">{title}</p>
                    <span className="order-count-badge">
                        {isFirstLoad ? "..." : orders?.length}
                    </span>
                </div>
            </div>

            <div className={`orders-grid`}>
                {isFirstLoad && skeletons.map((_, i) => (
                    <div key={i} className="order-card skeleton fade-pulse">
                        <div className="skeleton-line w-60"></div>
                        <div className="skeleton-line w-40"></div>
                        <div className="skeleton-line w-50"></div>
                        <div className="skeleton-line w-70"></div>
                    </div>
                ))}

                {isEmpty && (
                    <div className="empty-state fade-in">
                        <Inbox size={30} className="empty-icon" />
                        <div>Nothing to show</div>
                    </div>
                )}

                {!isFirstLoad && orders?.map((o) => {
                    const isUpdated = updated.some(u => u.id === o.order_id);
                    const isReady_For_pickup =
                        o?.live?.status === 'ready_for_pickup' &&
                        [2, 3].includes(o?.orderstatus_id);

                    return (
                        <React.Fragment key={o?.order_id}>
                            <div
                                onMouseEnter={() => isUpdated && onDismiss(o.order_id)}
                                className={`order-card fade-in ${isUpdated ? 'order-card--blinking' : ''} ${isReady_For_pickup && 'order-ready'}`}
                            >
                                <div className="order-info">
                                    <div
                                        onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.customer_id}`, "_blank")}
                                        className="order-customer">
                                        <span>Customer:</span> {o?.customer_name}
                                        {o?.customer_completed_orders && (
                                            <div className="orders-count">
                                                (
                                                <div className="order-complete-count">{o?.customer_completed_orders}</div>
                                                /
                                                <div className="order-cancelled-count">{o?.customer_cancelled_orders}</div>)
                                            </div>
                                        )}
                                    </div>

                                    <a href={`tel:${o?.customer_phone}`} className="order-phone">
                                        <span>Phone:</span> {o?.customer_phone}
                                    </a>
                                    <div className="order-id"
                                         onClick={() => window.open(`/order/${o?.unique_order_id}`, "_blank")}>
                                        <span>Order:</span> #{o?.unique_order_id?.slice(10)}
                                    </div>
                                    <div className="order-store"><span>Restaurant:</span> {o?.restaurant_name}</div>
                                    <div className="order-address"><span>Address:</span> {o?.order_addresss}</div>

                                    {o?.delivery_guy_name && (
                                        <>
                                            <div onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.delivery_guy_id}`, "_blank")} className="order-phone">
                                                <span>Delivery Guy:</span> {o?.delivery_guy_name} ( <span style={{fontSize: '14px', color: "#10b981", fontWeight: '600'}}>{o?.completed_orders_today}</span> / <span style={{fontSize: '14px', color: "#e65d5d", fontWeight: '600'}}>{o?.non_complete_order_count}</span> )
                                            </div>
                                            <a href={`tel:${o?.delivery_guy_phone}`} className="order-phone">
                                                <span>Phone:</span> {o?.delivery_guy_phone}
                                            </a>
                                        </>
                                    )}
                                </div>

                                <div className="order-footer">
                                    <div className="order-price">₹{o?.order_total}</div>
                                    <div className="payment-tag">{o?.payment_mode}</div>
                                    <div className="order-created">{o?.created_time}</div>
                                    <div className="order-updated">{o?.updated_time}</div>
                                </div>
                            </div>
                            <div className="div-bar"/>
                        </React.Fragment>
                    );
                })}
            </div>
        </section>
    );
};

const StatusDiv = ({ label, count, loading }) => {
    label = label?.replace(/_/g, ' ');
    const config = iconMap[label] || { icon: Package, color: "#64748b" };
    const Icon = config.icon;

    return (
        <div className="status-card">
            <div
                className="status-icon-wrapper"
                style={{ backgroundColor: `${config.color}15` }}
            >
                {loading ? (
                    <div className="skeleton-circle fade-pulse" />
                ) : (
                    <Icon size={20} color={config.color} />
                )}
            </div>

            <div className="status-content">
                {loading ? (
                    <>
                        <div className="skeleton-line w-40 fade-pulse" style={{ height: 14 }} />
                        <div className="skeleton-line w-60 fade-pulse" style={{ height: 10, marginTop: 6 }} />
                    </>
                ) : (
                    <>
                        <div className="count">{count}</div>
                        <div className="label">{label.toUpperCase()}</div>
                    </>
                )}
            </div>
        </div>
    );
};

const SortableItem = ({id, children}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id});

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        position: 'relative',
        marginTop: '10px',
        minWidth: 0,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <div className="drag-handle" {...listeners}>
                <div className="grip-dots">⋮⋮</div>
            </div>
            {children}
        </div>
    );
};

const iconMap = {
    "completed orders": { icon: CheckCircle2, color: "#10b981" },
    "cancelled orders": { icon: XCircle, color: "#ef4444" },
    "ongoing orders":   { icon: Truck,        color: "#3b82f6" },
    "total sales":      { icon: IndianRupee,   color: "#059669" },
    "on-time orders":   { icon: Timer,         color: "#8b5cf6" },
    "late orders":      { icon: Clock,         color: "#f59e0b" },
    "too late orders":  { icon: AlertTriangle, color: "#b91c1c" },
    "total orders":     { icon: Package,       color: "#64748b" },
    "accepted by store":{ icon: Store,         color: "#0ea5e9" },
    "accepted by admin":{ icon: UserCheck,     color: "#6366f1" },
    "ready by store":   { icon: BellRing,      color: "#ec4899" },
};


export default function Dashboard() {
    const [late, setLate] = useState([]);
    const [newOrders, setNewOrders] = useState([]);
    const [preparing, setPreparing] = useState([]);
    const [assigned, setAssigned] = useState([]);
    const [pickup, setPickup] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [awaiting, setAwaiting] = useState([]);
    const [cancelled, setCancelled] = useState([]);
    const [updatedOrders, setUpdatedOrders] = useState([]);
    const [cancelledByStore, setCancelledByStore] = useState([]);
    const [paymentFailed, setPaymentFailed] = useState([]);

    const [sectionOrder, setSectionOrder] = useState([
        "new", "preparing", "assigned", "pickup",
        "completed", "awaiting", "cancelled", "store_cancelled", "payment_failed"
    ]);

    const [dashboard, setDashboard] = useState(null);
    const [availableBoys, setAvailableBoys] = useState([]);

    // dismissOrder removes the order from updatedOrders
    const { dismissOrder, muted, toggleMute } = useOrderAlerts(updatedOrders, setUpdatedOrders);

    const [loading, setLoading] = useState({
        late: true, new: true, preparing: true, assigned: true,
        pickup: true, completed: true, awaiting: true, cancelled: true,
        store_cancelled: true, payment_failed: true, boys: true, feed: true
    });

    const resetLoading = (status) => setLoading({
        late: status, new: status, preparing: status, assigned: status,
        pickup: status, completed: status, awaiting: status, cancelled: status,
        store_cancelled: status, payment_failed: status, boys: status, feed: status,
    });

    const loadData = useCallback(() => {
        resetLoading(true);

        const newUpdates = new Map();
        let latestNewOrders, latestPreparing, latestAssigned, latestCancelled, latestCancelledByStore;

        Promise.allSettled([
            fetchNewOrders().then(data => {
                setNewOrders(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    data?.forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'new_order');
                    });
                    latestNewOrders = data;
                    return data;
                });
            }),
            fetchPreparingOrders().then(data => {
                setPreparing(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    data?.filter(n => n.live?.status === "ready_for_pickup").forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'ready_order');
                    });
                    latestPreparing = data;
                    return data;
                });
            }),
            fetchAssignedOrders().then(data => {
                setAssigned(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    data?.filter(n => n.live?.status === "ready_for_pickup").forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'ready_order');
                    });
                    latestAssigned = data;
                    return data;
                });
            }),
            fetchCancelledOrders().then(data => {
                const paymentFailedOrders = data?.filter(o => o?.order_type === "payment_failed");
                const cancelledByStoreOrders = data?.filter(o => o?.order_type === "store_cancelled");
                const cancelledByCustomerAndAdmin = data?.filter(o => o?.order_type === "cancelled");


                setCancelledByStore(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    cancelledByStoreOrders?.forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'cancelled_by_store');
                    });
                    latestCancelledByStore = cancelledByStoreOrders;
                    return cancelledByStoreOrders;
                });

                setPaymentFailed(paymentFailedOrders);
                setCancelled(prev => {
                    const prevIds = new Set(prev?.map(o => o.unique_order_id) ?? []);
                    cancelledByCustomerAndAdmin?.forEach(o => {
                        if (!prevIds.has(o.unique_order_id)) newUpdates.set(o.unique_order_id, 'cancelled');
                    });
                    latestCancelled = cancelledByCustomerAndAdmin;
                    return cancelledByCustomerAndAdmin;
                });
            }),

            fetchLateOrders().then(setLate),
            fetchPickedUpOrders().then(setPickup),
            fetchCompletedOrders().then(setCompleted),
            fetchAwaitingOrders().then(setAwaiting),
            fetchDataFeed().then(setDashboard),
        ]).finally(() => {
            const allCurrentOrders = new Map([
                ...(latestNewOrders?.map(o => [o.unique_order_id, 'new_order']) ?? []),
                ...(latestPreparing?.map(o => [o.unique_order_id, 'ready_order']) ?? []),
                ...(latestAssigned?.map(o => [o.unique_order_id, 'ready_order']) ?? []),
                ...(latestCancelled?.map(o => [o.unique_order_id, 'cancelled']) ?? []),
                ...(latestCancelledByStore?.map(o => [o.unique_order_id, 'cancelled_by_store']) ?? [])
            ]);

            setUpdatedOrders(prev => {
                const merged = new Map(newUpdates);

                prev?.forEach(({ id, status }) => {
                    if (!merged.has(id)) {
                        if (!allCurrentOrders.has(id)) return; // gone — remove
                        const currentStatus = allCurrentOrders.get(id);
                        merged.set(id, currentStatus !== status ? currentStatus : status);
                    }
                });

                return Array.from(merged, ([id, status]) => ({ id, status }));
            });

            resetLoading(false);
        });

    }, []);

    const loadAvailableBoys = useCallback(() => {
        fetchAvailableBoys().then(setAvailableBoys);
    }, []);

    useEffect(() => {
        loadData();
        loadAvailableBoys();

        const mainInterval = setInterval(loadData, 30000);
        const boysInterval = setInterval(loadAvailableBoys, 10000);

        return () => {
            clearInterval(mainInterval);
            clearInterval(boysInterval);
        };
    }, [loadData, loadAvailableBoys]);

    const sectionMap = {
        new:            { title: "New Orders",       data: newOrders,      isLoading: loading.new },
        preparing:      { title: "Preparing Orders", data: preparing,      isLoading: loading.preparing },
        assigned:       { title: "Assigned Orders",  data: assigned,       isLoading: loading.assigned },
        pickup:         { title: "Pick Up Orders",   data: pickup,         isLoading: loading.pickup },
        completed:      { title: "Completed Orders", data: completed,      isLoading: loading.completed },
        awaiting:       { title: "Awaiting Payments",data: awaiting,       isLoading: loading.awaiting },
        cancelled:      { title: "Cancelled Orders", data: cancelled,      isLoading: loading.cancelled },
        store_cancelled:{ title: "Store Cancelled",  data: cancelledByStore, isLoading: loading?.store_cancelled },
        payment_failed: { title: "Payment Failed",   data: paymentFailed,  isLoading: loading?.payment_failed },
    };

    const sections = sectionOrder.map(id => ({ id, ...sectionMap[id] }));

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setSectionOrder((prev) => {
            const oldIndex = prev.indexOf(active.id);
            const newIndex = prev.indexOf(over.id);
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    return (
        <div className="dashboard">

        <div className="dashboard-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <p className="dashboard-title">Live orders</p>

            {/* Notification bell — hover reveals the dropdown panel */}
            <div style={{ position: "relative" }}>
                <NotificationToaster
                    updatedOrders={updatedOrders}
                    onDismissOrder={dismissOrder}
                    muted={muted}
                    onToggleMute={toggleMute}
                />
            </div>
        </div>



            <section className="stats-grid">
                {(loading?.feed && (!dashboard || dashboard.length === 0))
                    ? Array.from({length: 11}).map((_, i) => (
                        <StatusDiv key={i} label="loading" count={0} loading={true}/>
                    ))
                    : Object.entries(dashboard || {}).map(([key, value]) => (
                        <StatusDiv key={key} label={key} count={value} loading={false}/>
                    ))}
            </section>

            <section className="section">
                <div className="section-header section-header-modern">
                    <p className="section-title">Available Partners</p>
                    <span>{availableBoys?.length}</span>
                </div>

                <div className="partners-grid">
                    {(loading.boys && (!availableBoys || availableBoys.length === 0))
                        ? Array.from({length: 4}).map((_, i) => (
                            <AvailablePartnersDiv key={i} loading/>
                        ))
                        : availableBoys?.map((b, i) => (
                            <AvailablePartnersDiv
                                key={i}
                                id={b?.user_id}
                                heading={b?.detail?.heading}
                                name={b?.detail?.name}
                                photo={b?.detail?.photo}
                                phone={b?.phone || 'N/A'}
                                pending_orders={b?.non_complete_orders}
                                completed_orders={b?.completed_orders}
                                latitude={b?.detail?.delivery_lat}
                                longitude={b?.detail?.delivery_long}
                            />
                        ))
                    }
                </div>
            </section>

            <section className="section">
                <div className="section-header section-header-modern">
                    <p className="section-title">Getting Late</p>
                    <span>{late?.length}</span>
                </div>

                <div className="partners-grid">
                    {(loading.late && (!late || late.length === 0))
                        ? Array.from({length: 4}).map((_, i) => (
                            <AvailablePartnersDiv key={i} loading/>
                        ))
                        : late?.map((o, i) => (
                            <React.Fragment key={o?.order_id}>
                                <div
                                    className={`order-card fade-in`}
                                    style={{ borderColor: "#cbd5e1", borderWidth: 2 }}
                                >
                                    <div className="order-info">
                                        <div onClick={() => window.open(`https://food.totalfresh.co/public/admin/user/edit/${o?.customer_id}`, "_blank")} className="order-customer">
                                            <span>Customer:</span> {o?.customer_name}
                                            <div className="orders-count">
                                                (
                                                <div className="order-complete-count">{o?.customer_completed_orders}</div>
                                                /
                                                <div className="order-cancelled-count">{o?.customer_cancelled_orders}</div>)
                                            </div>
                                        </div>

                                        <a href={`tel:${o?.customer_phone}`} className="order-phone">
                                            <span>Phone:</span> {o?.customer_phone}
                                        </a>
                                        <div className="order-id" onClick={() => window.open(`/order/${o?.unique_order_id}`, "_blank")}><span>Order:</span> #{o?.unique_order_id?.slice(10)}</div>
                                        <div className="order-store"><span>Restaurant:</span> {o?.restaurant_name}</div>
                                        <div
                                            className="order-html"
                                            dangerouslySetInnerHTML={{__html: o?.delivery_details}}
                                        />
                                    </div>

                                    <div className="order-footer">
                                        <div className="order-price">₹{o?.order_total}</div>
                                        <div className="payment-tag">{o?.payment_mode}</div>
                                        <div className="order-created">{o?.created_time}</div>
                                        <div className="order-updated">{o?.updated_time}</div>
                                    </div>
                                </div>
                            </React.Fragment>
                        ))}
                </div>
            </section>

            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={sectionOrder} strategy={rectSortingStrategy}>
                    <div className="dashboard-container">
                        {sections.map((section) => (
                            <SortableItem key={section.id} id={section.id}>
                                <OrderSection
                                    onDismiss={dismissOrder}
                                    updated={updatedOrders}
                                    title={section.title}
                                    orders={section.data}
                                    loading={section.isLoading}
                                />
                            </SortableItem>
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            <TrackingMap points={availableBoys} orderPoints={[...newOrders, ...preparing]}/>
        </div>
    );
}